import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { COLOR } from '../../../shared/shared';
import {
	Rect,
	computeSnapLines,
	trySnapRectNearPosition,
	detectCollision,
	getBestSnap,
	generateMultipickDivLines,
	generateMultipickGripperLines,
} from '../../layout_utils';
import { LayoutBounds, ModelRect, SelectMode } from '../utilities/types';
import { MultiPickType } from 'blackbox';
import { LabelRect } from './LabelRect';

const DOUBLE_TAP_THRESHOLD: number = 300;

function applyGap<T extends Rect>(pRect: T, pGapDistance: number): T {
	const lRect = { ...pRect };
	lRect.height += pGapDistance;
	lRect.width += pGapDistance;
	lRect.x -= pGapDistance / 2;
	lRect.y -= pGapDistance / 2;
	return lRect;
}

function _applyMultipickSize(pRect: ModelRect) {
	const lRect = { ...pRect };
	const rotated = !!lRect.rotated;
	const multipick = lRect.multipick || 0;
	const multipickAlongWidth = !!lRect.multipickAlongWidth;

	if ((rotated && !multipickAlongWidth) || (!rotated && multipickAlongWidth)) {
		lRect.width *= multipick + 1;
	} else {
		lRect.height *= multipick + 1;
	}
	return lRect;
}

export interface DraggableRectProps {
	otherRects: ModelRect[];
	gapDistance: number /* gap distance svg scale */;
	rect: ModelRect;
	updatePosition: (index: number, x: number, y: number) => void;
	index: number;
	enabled: boolean;
	handleGroupDrag: (i: number, dx: number, xy: number) => boolean;
	pUpdateDisplayPosition(pIndex: number, pX: number, pY: number): void;
	snapStrength: number;
	dragBounds: LayoutBounds;
	onDoubleClick: (index: number) => void;
	selectMode?: SelectMode;
	groupIndexes: number[];
	youAreInGroup: boolean;
}

export const DraggableRect = React.memo(function DraggableRect({
	rect,
	updatePosition,
	gapDistance,
	index,
	enabled,
	otherRects,
	handleGroupDrag,
	snapStrength,
	pUpdateDisplayPosition,
	dragBounds,
	onDoubleClick,
	selectMode,
	groupIndexes,
	youAreInGroup,
}: DraggableRectProps) {
	const [lEditingRect, lSetEditingRect] = useState<ModelRect>(rect);
	const [lPointerDownTime, lSetPointerDownTime] = useState<number | null>(null);
	const [, setTapCount] = useState<number>(0);
	const [lastTapTime, setLastTapTime] = useState<number>(0);
	const lIsActive = lPointerDownTime !== null;
	const lRef = useRef<SVGRectElement | null>(null);

	const [currentSnapXLine, setCurrentSnapXLine] = useState<number | null>(null);
	const [currentSnapYLine, setCurrentSnapYLine] = useState<number | null>(null);
	const [groupBoundingBox, setGroupBoundingBox] = useState<Rect | null>(null);
	const [isMultiSelect, setIsMultiSelect] = useState<boolean>(false);

	useEffect(() => {
		setIsMultiSelect(selectMode === SelectMode.Multi);
	}, [selectMode]);

	useEffect(() => {
		const r = applyGap(_applyMultipickSize(rect), gapDistance);
		lSetEditingRect(r);
	}, [rect, gapDistance]);

	useEffect(() => {
		if (!groupIndexes.length || !lIsActive || !isMultiSelect) {
			setGroupBoundingBox(null);
			return;
		}
		const boxes = [...groupIndexes.map(i => otherRects[i])].map(r =>
			applyGap(_applyMultipickSize(r), gapDistance)
		);
		boxes.push(lEditingRect);

		const x = Math.min(...boxes.map(b => b.x));
		const y = Math.min(...boxes.map(b => b.y));
		const x2 = Math.max(...boxes.map(b => b.x + b.width));
		const y2 = Math.max(...boxes.map(b => b.y + b.height));

		setGroupBoundingBox({ x, y, width: x2 - x, height: y2 - y } as ModelRect);
	}, [groupIndexes, otherRects, lEditingRect, gapDistance, lIsActive]);

	const generateTapeLines = (pEditingRect: ModelRect): ModelRect[] => {
		// Add one tape line starting at pEditingRectY.
		const lRes = [];
		const rotated = !!pEditingRect.rotated;
		const acrossWidth = !!pEditingRect.multipickAlongWidth;
		const lNumDivs = (pEditingRect.multipick || 0) + 1;

		const lAligned = (!rotated && !acrossWidth) || (rotated && acrossWidth);

		const lDivMajorSize =
			(lAligned ? pEditingRect.height : pEditingRect.width) / lNumDivs;

		const lDivMinorSize = lAligned ? pEditingRect.width : pEditingRect.height;

		const lTapeLength = acrossWidth ? lDivMinorSize * 0.5 : lDivMajorSize * 0.5;
		const lPadding = acrossWidth ? lDivMinorSize * 0.25 : lDivMajorSize * 0.25;
		const lTapeWidth = (acrossWidth ? lDivMajorSize : lDivMinorSize) * 0.01;
		const lMidLine = (acrossWidth ? lDivMajorSize : lDivMinorSize) / 2;

		for (let i = 0; i < lNumDivs; i++) {
			const lXOffset = lAligned ? 0 : lDivMajorSize * i;
			const lYOffset = lAligned ? lDivMajorSize * i : 0;

			const lSlit = {
				...pEditingRect,
				x: pEditingRect.x + (rotated ? lPadding : lMidLine) + lXOffset,
				y: pEditingRect.y + (rotated ? lMidLine : lPadding) + lYOffset,
				width: rotated ? lTapeLength : lTapeWidth,
				height: rotated ? lTapeWidth : lTapeLength,
			};
			lRes.push(lSlit);
		}

		return lRes;
	};

	const xl = dragBounds.min.x;
	const xh = dragBounds.max.x;
	const yl = dragBounds.min.y;
	const yh = dragBounds.max.y;

	const allOtherBoxes: Rect[] = otherRects.map(pOtherRect =>
		applyGap(_applyMultipickSize(pOtherRect), gapDistance)
	);

	// Compute snap lines from current layout (other boxes + pallet)
	const otherBoxesNotInGroup = allOtherBoxes.filter(
		(_, i) => !groupIndexes.includes(i)
	);
	const snapLines = computeSnapLines(
		isMultiSelect ? otherBoxesNotInGroup : allOtherBoxes,
		{ xl, xh, yl, yh }
	);

	const getRectPositionWithBounds = useCallback(
		(rect: ModelRect): ModelRect => {
			const x = Math.min(
				Math.max(dragBounds.min.x, rect.x),
				dragBounds.max.x - rect.width
			);
			const y = Math.min(
				Math.max(dragBounds.min.y, rect.y),
				dragBounds.max.y - rect.height
			);
			return { ...rect, x, y };
		},
		[dragBounds]
	);

	const lUpdateSavedPosition = () =>
		/* backpropagate saved rect position */
		updatePosition(
			index,
			lEditingRect.x + gapDistance / 2,
			lEditingRect.y + gapDistance / 2
		);

	const resolveCollisions = (finalRect: ModelRect): ModelRect | null => {
		const correction = trySnapRectNearPosition(
			finalRect,
			otherBoxesNotInGroup,
			snapLines,
			snapStrength * 7,
			dragBounds
		);
		return correction || null;
	};

	const snapGroupBoundingBox = (
		bbox: Rect,
		snapStrength: number
	): { rect: Rect; xCandidate?: any; yCandidate?: any } | null => {
		const groupRect: ModelRect = {
			x: bbox.x,
			y: bbox.y,
			width: bbox.width,
			height: bbox.height,
			fill: 'none',
		} as ModelRect;
		const best = getBestSnap(
			groupRect,
			snapLines,
			snapStrength,
			otherBoxesNotInGroup
		);
		if (!best) {
			return null;
		}
		// `best.rect` is a ModelRect; convert it back to a plain Rect
		return {
			rect: {
				x: best.rect.x,
				y: best.rect.y,
				width: best.rect.width,
				height: best.rect.height,
			} as Rect,
			xCandidate: best.xCandidate,
			yCandidate: best.yCandidate,
		};
	};

	/**
	 * Pointer event logic
	 */
	const lPointerActions = {
		onPointerDown: function (pEvent: React.PointerEvent) {
			if (!enabled) return;
			if (lRef.current !== null) lRef.current.setPointerCapture(pEvent.pointerId);

			const lNow = new Date().getTime(),
				bb = (pEvent.target as any).getBoundingClientRect(),
				x = pEvent.clientX - bb.left,
				y = pEvent.clientY - bb.top,
				offset = { x, y };
			lSetEditingRect({
				...lEditingRect,
				offset,
			});
			lSetPointerDownTime(lNow);
			// Clear any existing snap lines when starting a new drag
			setCurrentSnapXLine(null);
			setCurrentSnapYLine(null);
		},
		onPointerMove: function (pEvent: React.PointerEvent) {
			if (!lIsActive) return;
			const bb = (pEvent.target as any).getBoundingClientRect();
			let x = pEvent.clientX - bb.left;
			let y = pEvent.clientY - bb.top;
			const { offset } = lEditingRect;
			let dx = offset.x - x;
			let dy = offset.y - y;

			// If we have a bounding box for the group, let's do bounding-box-level dragging.
			if (isMultiSelect && groupBoundingBox && groupIndexes.length > 0) {
				// Potential new bounding box
				let potentialBBox = {
					...groupBoundingBox,
					x: groupBoundingBox.x - dx,
					y: groupBoundingBox.y - dy,
				};

				// Bound-check the bounding box
				potentialBBox = getRectPositionWithBounds(potentialBBox as ModelRect);

				// Attempt group snap
				const bestSnap = snapStrength
					? snapGroupBoundingBox(potentialBBox, snapStrength)
					: null;

				let finalBBox = potentialBBox;
				if (bestSnap) {
					finalBBox = {
						x: bestSnap.rect.x,
						y: bestSnap.rect.y,
						width: bestSnap.rect.width,
						height: bestSnap.rect.height,
					} as ModelRect;
					setCurrentSnapXLine(bestSnap.xCandidate?.line?.position ?? null);
					setCurrentSnapYLine(bestSnap.yCandidate?.line?.position ?? null);
				} else {
					// If no snap found, clear snap lines
					setCurrentSnapXLine(null);
					setCurrentSnapYLine(null);
				}

				// Compute how much the bounding box moved from groupBoundingBox
				const bboxDeltaX = finalBBox.x - groupBoundingBox.x;
				const bboxDeltaY = finalBBox.y - groupBoundingBox.y;

				// Attempt to move all boxes (including the “leader”)
				if (handleGroupDrag(index, -bboxDeltaX, -bboxDeltaY)) {
					// move this “leader” box by the same offset so it stays in sync.
					const newLeaderRect = {
						...lEditingRect,
						x: lEditingRect.x + bboxDeltaX,
						y: lEditingRect.y + bboxDeltaY,
					};
					// Now clamp the leader to bounds again
					const withBounds = getRectPositionWithBounds(newLeaderRect);
					lSetEditingRect(withBounds);

					// Update our local bounding box so that subsequent pointermoves
					// keep using the newly snapped bounding box.
					setGroupBoundingBox({
						...finalBBox,
						x: finalBBox.x,
						y: finalBBox.y,
					});
				}
				return;
			} else {
				// SINGLE-BOX DRAGGING (no group, or no bounding box)
				const newR = {
					...lEditingRect,
					x: lEditingRect.x - dx,
					y: lEditingRect.y - dy,
				};
				const rectWithBounds = getRectPositionWithBounds(newR);
				if (snapStrength === 0) {
					lSetEditingRect(rectWithBounds);
					return;
				}

				const bestSnap = getBestSnap(
					rectWithBounds,
					snapLines,
					snapStrength,
					allOtherBoxes
				);
				if (!bestSnap) {
					lSetEditingRect(rectWithBounds);
					pUpdateDisplayPosition(index, rectWithBounds.x, rectWithBounds.y);
					setCurrentSnapXLine(null);
					setCurrentSnapYLine(null);
					return;
				}
				lSetEditingRect(bestSnap.rect);
				pUpdateDisplayPosition(index, bestSnap.rect.x, bestSnap.rect.y);

				setCurrentSnapXLine(bestSnap.xCandidate?.line?.position ?? null);
				setCurrentSnapYLine(bestSnap.yCandidate?.line?.position ?? null);
			}
		},
		onPointerUp: function (pEvent: React.PointerEvent) {
			if (!enabled) return;
			if (lRef.current) {
				lRef.current.releasePointerCapture(pEvent.pointerId);
			}
			const lNow = new Date().getTime();
			if (!isMultiSelect) {
				if (lastTapTime && lNow - lastTapTime < DOUBLE_TAP_THRESHOLD) {
					// Double-Tap Detected
					onDoubleClick(index);
					setTapCount(0);
					setLastTapTime(0);
					return;
				}
			}
			setTapCount(1);
			setLastTapTime(lNow);
			setTimeout(() => {
				setTapCount(0);
			}, DOUBLE_TAP_THRESHOLD);
			lSetPointerDownTime(null);
			const finalRect = lEditingRect;

			// Check if finalRect collides with any of them
			const isColliding = otherBoxesNotInGroup.some(other =>
				detectCollision(finalRect, other)
			);

			if (isColliding && !youAreInGroup) {
				// Attempt a “corrective snap”
				const correction = resolveCollisions(finalRect);
				if (correction) {
					// We found a close-by, non-overlapping snapped position
					lSetEditingRect(correction);
					// persist to parent
					updatePosition(
						index,
						correction.x + gapDistance / 2,
						correction.y + gapDistance / 2
					);
				} else {
					lUpdateSavedPosition();
				}
			} else {
				lUpdateSavedPosition();
			}
			setCurrentSnapXLine(null);
			setCurrentSnapYLine(null);
		},
		onPointerLeave: () => {},
		onPointerCancel: () => {
			lSetPointerDownTime(null);
			// Clear snap lines if pointer is canceled
			setCurrentSnapXLine(null);
			setCurrentSnapYLine(null);
		},
		onGotPointerCapture: () => {},
		onLostPointerCapture: () => {},
	};

	let lFillColor = rect.fill,
		lStrokeColor = String(COLOR.CARDBOARD);
	if (lIsActive) {
		lFillColor = String(COLOR.MOVE_BOX);
		lStrokeColor = String(COLOR.WHITE);
	}
	if (enabled) {
		if (youAreInGroup) {
			for (const other of otherBoxesNotInGroup) {
				if (detectCollision(lEditingRect, other)) {
					lFillColor = COLOR.RED_CLEAR;
					break;
				}
			}
		} else {
			for (const other of allOtherBoxes) {
				if (detectCollision(lEditingRect, other)) {
					lFillColor = COLOR.RED_CLEAR;
					break;
				}
			}
		}
	}

	const lSecondRect: ModelRect = applyGap(lEditingRect, -gapDistance);

	function omitExtraProps<T extends ModelRect>(props: T) {
		const { rotated, multipickAlongWidth, spin, labelSide, ...restProps } = props;
		return restProps;
	}
	const mp_divisions = generateMultipickDivLines(
		{
			x: lEditingRect.x,
			y: lEditingRect.y,
			width: lEditingRect.width,
			height: lEditingRect.height,
		},
		lEditingRect.multipick || 0,
		!!lEditingRect.multipickAlongWidth,
		!!lEditingRect.rotated
	);

	const gripperLines = generateMultipickGripperLines(
		{
			x: lEditingRect.x + 1, // Adding an additional pixel here to account for strokewidth
			y: lEditingRect.y + 1, // Same here
			width: lEditingRect.width,
			height: lEditingRect.height,
		},
		lEditingRect.multipick || 0,
		!!lEditingRect.multipickAlongWidth,
		!!lEditingRect.rotated
	);

	const tape_lines = generateTapeLines(lEditingRect);

	const editingRectSVGProps = omitExtraProps(lEditingRect);
	const {
		x: editingRectX,
		y: editingRectY,
		...editingBoxSVGProps
	} = editingRectSVGProps;

	const factor = Math.min(
		dragBounds.max.x - dragBounds.min.x,
		dragBounds.max.y - dragBounds.min.y
	);

	return (
		<Fragment>
			<rect
				{...omitExtraProps(lSecondRect)}
				fill={lFillColor}
				stroke={lStrokeColor}
			/>
			<svg x={editingRectX} y={editingRectY}>
				<rect
					{...editingBoxSVGProps}
					ref={lRef}
					fill={lFillColor}
					fillOpacity={0}
					stroke={lStrokeColor}
					{...lPointerActions}
				/>
				{lEditingRect.multipick === undefined ||
				lEditingRect.multipick === MultiPickType.None ? (
					<LabelRect box={lEditingRect} />
				) : (
					Array(lEditingRect.multipick + 1)
						.fill(0)
						.map((_, i) => (
							<LabelRect key={i} box={lEditingRect} multipickLabelNumber={i} />
						))
				)}
			</svg>
			{mp_divisions.map((pDiv, pIdx) => {
				return <rect key={pIdx} {...pDiv} stroke={lStrokeColor} />;
			})}
			{tape_lines.map((pLine, pIdx) => {
				return (
					<rect
						key={pIdx}
						{...omitExtraProps(pLine)}
						fill={lStrokeColor}
						stroke={String(COLOR.DARK_PLANK)}
					/>
				);
			})}
			{gripperLines !== null && (
				<>
					<line
						{...gripperLines.line}
						strokeWidth={factor * 0.007}
						stroke={COLOR.SKY_BLUE}
					/>
					{gripperLines.circles.map((circle, i) => (
						<circle key={i} fill={COLOR.WHITE} r={factor * 0.01} {...circle} />
					))}
				</>
			)}
			<svg>
				{currentSnapXLine !== null && (
					<line
						x1={currentSnapXLine}
						y1={dragBounds.min.y}
						x2={currentSnapXLine}
						y2={dragBounds.max.y}
						stroke={'#1c344f'}
						strokeWidth={2}
						strokeDasharray="4 2"
					/>
				)}
				{currentSnapYLine !== null && (
					<line
						x1={dragBounds.min.x}
						y1={currentSnapYLine}
						x2={dragBounds.max.x}
						y2={currentSnapYLine}
						stroke={'#1c344f'}
						strokeWidth={2}
						strokeDasharray="4 2"
					/>
				)}
			</svg>
			<svg>
				{groupBoundingBox && (
					<rect
						x={groupBoundingBox.x}
						y={groupBoundingBox.y}
						width={groupBoundingBox.width}
						height={groupBoundingBox.height}
						fill="none"
						stroke="#1890ff"
						strokeWidth={2}
						strokeDasharray="4"
					/>
				)}
			</svg>
		</Fragment>
	);
});
